// Social links configuration for the footer.
//
// Session 44: URLs are placeholders. The interface is stable so future
// backends (admin API / env injection / remote config) can replace values
// without touching UI code.

import { Mail } from 'lucide-react';
import { WeChatIcon, BilibiliIcon, DouyinIcon, XiaohongshuIcon } from '../components/icons/social-icons.js';
import type { DictionaryKey } from '@examforge/shared/i18n';

/** Minimal icon props — shared by both lucide and custom SVG icons. */
export interface IconProps {
  size?: number | string;
  className?: string;
}

/** Any component accepting { size?, className? } — lucide or custom. */
type IconComponent = React.ComponentType<IconProps>;

export interface SocialLink {
  /** Unique key for React key prop and future lookups. */
  key: string;
  /** Display URL — placeholder '#' until real accounts are set up. */
  url: string;
  /** Icon component — lucide or custom SVG, same API. */
  icon: IconComponent;
  /** i18n dictionary key for the link label. */
  labelKey: DictionaryKey;
  /** CSS value for hover brand color. References a CSS custom property
   *  defined in :root (light) and [data-theme="dark"]. Omit for default. */
  brandHover?: string;
}

/**
 * Footer social links — rendered from config, not hardcoded.
 *
 * To add a new platform:
 *   1. Add icon component to components/icons/social-icons.tsx
 *   2. Add i18n key to zh.ts + en.ts + DictionaryKey
 *   3. Append entry here
 *
 * To switch to runtime config:
 *   Replace this array with a fetch() call or env-var read.
 *   The SocialLink interface stays the same.
 */
export const SOCIAL_LINKS: readonly SocialLink[] = [
  { key: 'email',       url: '#', icon: Mail,             labelKey: 'footer.email' },
  { key: 'wechat',      url: '#', icon: WeChatIcon,       labelKey: 'footer.wechat',      brandHover: 'var(--brand-wechat)' },
  { key: 'bilibili',    url: '#', icon: BilibiliIcon,     labelKey: 'footer.bilibili',    brandHover: 'var(--brand-bilibili)' },
  { key: 'douyin',      url: '#', icon: DouyinIcon,       labelKey: 'footer.douyin',      brandHover: 'var(--brand-douyin)' },
  { key: 'xiaohongshu', url: '#', icon: XiaohongshuIcon, labelKey: 'footer.xiaohongshu', brandHover: 'var(--brand-xiaohongshu)' },
];
